import { IRowNode } from "@ag-grid-community/core";
import { CheckIcon } from "@chakra-ui/icons";
import { format as dateFnsFormat, parseISO as dateFnsParseISO } from "date-fns";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableCellFormField } from "gen/model/genericTableCellFormField";
import { GenericTableCellValue } from "gen/model/genericTableCellValue";
import { GenericTableRow } from "gen/model/genericTableRow";
import { ReactNode } from "react";
import { RenderCellValueFn } from "shared/components/table/data/DataTableTypes";
import {
  DataTableCellEditorBoolean,
  DataTableCellEditorDate,
  DataTableCellEditorMoney,
  DataTableCellEditorPercent,
  DataTableCellEditorSelect,
  DataTableCellEditorText,
} from "shared/components/table/data/internal/DataTableCellEditors";
import {
  formatIsoDatetimeToDisplay,
  formatIsoDateToDisplay,
} from "shared/utils/DateUtils";
import { formatDecimalForTable } from "shared/utils/DecimalUtils";
import { formatPercentageForTable } from "shared/utils/formattingUtils";
import {
  formatMoneyStringForTable,
  parseCurrency,
} from "shared/utils/MoneyUtils";

type DataTableCellValueRenderer<
  T extends string = GenericTableCellValue["type"],
> = (
  cell: Omit<GenericTableCell, "value"> & {
    value: Extract<
      GenericTableCellValue,
      {
        type: T;
      }
    >;
  },
) => ReactNode;

type DataTableCellValueRenderers<
  T extends string = GenericTableCellValue["type"],
> = {
  [K in T]: DataTableCellValueRenderer<K>;
};

export const defaultDataTableRenderCellValue: RenderCellValueFn = (
  { tableContext, cell, row },
  fieldDisabled,
  params,
) => {
  const cellValueRenderers: DataTableCellValueRenderers = {
    text: ({ value: { text }, form_field }) => {
      if (form_field?.options) {
        return (
          <DataTableCellEditorSelect
            value={text ?? ""}
            formField={form_field}
            isDisabled={isFieldDisabled(fieldDisabled, form_field, row)}
            shouldUnregister={tableContext.shouldUnregisterFormFields}
            params={params}
          />
        );
      } else if (form_field) {
        return (
          <DataTableCellEditorText
            value={text ?? ""}
            formField={form_field}
            isDisabled={isFieldDisabled(fieldDisabled, form_field, row)}
            shouldUnregister={tableContext.shouldUnregisterFormFields}
            params={params}
          />
        );
      }
      return text ?? null;
    },
    formula: ({ value: { text }, form_field }) => {
      if (form_field?.options) {
        return (
          <DataTableCellEditorSelect
            value={text}
            formField={form_field}
            isDisabled={isFieldDisabled(fieldDisabled, form_field, row)}
            shouldUnregister={tableContext.shouldUnregisterFormFields}
            params={params}
          />
        );
      } else if (form_field) {
        return (
          <DataTableCellEditorText
            value={text}
            formField={form_field}
            isDisabled={isFieldDisabled(fieldDisabled, form_field, row)}
            shouldUnregister={tableContext.shouldUnregisterFormFields}
            params={params}
          />
        );
      }
      return text;
    },
    boolean: ({ value: { value }, form_field }) => {
      if (form_field) {
        return (
          <DataTableCellEditorBoolean
            value={value}
            formField={form_field}
            isDisabled={isFieldDisabled(fieldDisabled, form_field, row)}
            shouldUnregister={tableContext.shouldUnregisterFormFields}
            params={params}
          />
        );
      }
      return value ? <CheckIcon color="primary.400" w="18px" h="18px" /> : null;
    },
    date: ({ value: { format, date }, form_field }) => {
      if (form_field) {
        return (
          <DataTableCellEditorDate
            value={date}
            formField={form_field}
            isDisabled={isFieldDisabled(fieldDisabled, form_field, row)}
            shouldUnregister={tableContext.shouldUnregisterFormFields}
            params={params}
          />
        );
      }
      return format
        ? dateFnsFormat(dateFnsParseISO(date), format)
        : formatIsoDateToDisplay(date);
    },
    datetime: ({ value: { format, datetime } }) =>
      format
        ? dateFnsFormat(dateFnsParseISO(datetime), format)
        : formatIsoDatetimeToDisplay(datetime),
    money: ({ value, form_field }) => {
      if (form_field) {
        return (
          <DataTableCellEditorMoney
            value={value}
            formField={form_field}
            isDisabled={isFieldDisabled(fieldDisabled, form_field, row)}
            shouldUnregister={tableContext.shouldUnregisterFormFields}
            params={params}
          />
        );
      }
      const currency = parseCurrency(value.currency?.toUpperCase());
      return formatMoneyStringForTable(tableContext, value.amount, currency);
    },
    decimal: ({ value: { value } }) => formatDecimalForTable(value),
    integer: ({ value: { value } }) => Number(value),
    percent: ({ value: { value }, form_field }) => {
      if (form_field) {
        return (
          <DataTableCellEditorPercent
            value={value}
            formField={form_field}
            isDisabled={isFieldDisabled(fieldDisabled, form_field, row)}
            shouldUnregister={tableContext.shouldUnregisterFormFields}
            params={params}
          />
        );
      }
      return formatPercentageForTable(value);
    },
  };

  if (!cell.value) {
    return null;
  }

  if (!(cell.value.type in cellValueRenderers)) {
    throw new Error(
      `No cell value renderer for type "${cell.value.type}" found.`,
    );
  }

  return cellValueRenderers[cell.value.type](cell as never);
};

const isFieldDisabled = (
  fieldDisabled: boolean | undefined,
  form_field: GenericTableCellFormField,
  row: IRowNode<GenericTableRow>,
) =>
  fieldDisabled ??
  ((row.selectable && !row.isSelected()) || form_field.is_editable === false);
